import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { Megaphone, Users, ChartLineUp, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

const BENEFITS = [
  { icon: Users, title: "Vetted candidates", text: "Every applicant is reviewed by our team before we introduce them to you." },
  { icon: Megaphone, title: "Wide reach", text: "Your roles reach active job seekers across the UK and US." },
  { icon: ChartLineUp, title: "Feed integration", text: "Send us your vacancies via XML feed and we handle the rest." },
];

export default function Employers() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", enquiry_type: "advertise", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not send enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App bg-secondary/40">
      <Navbar />

      <section className="bg-navy py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-accent">For Employers</p>
          <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            Hire faster with VacancyNetwork
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/60">
            Want to advertise your vacancies or have a question? Send us an enquiry and our team will get back to you.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 md:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <h2 className="font-heading text-2xl font-bold">Why work with us</h2>
            <div className="mt-6 space-y-5">
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <b.icon size={22} weight="fill" />
                  </span>
                  <div>
                    <h3 className="font-semibold">{b.title}</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl bg-accent/10 p-4 text-sm text-foreground/80">
              <ShieldCheck size={18} weight="fill" className="mb-1 inline text-accent" /> We treat every enquiry
              confidentially and only use your details to respond to you.
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            {done ? (
              <div className="py-10 text-center" data-testid="enquiry-success">
                <CheckCircle size={56} weight="fill" className="mx-auto text-accent" />
                <h3 className="mt-4 font-heading text-2xl font-bold">Thanks, we've got it</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your enquiry has been sent to our team. We'll be in touch at {form.email} shortly.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-xl font-bold">Send an enquiry</h2>
                <form onSubmit={submit} className="mt-5 space-y-4" data-testid="enquiry-form">
                  {error && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="enquiry-error">
                      {error}
                    </div>
                  )}
                  <div>
                    <Label>What can we help with?</Label>
                    <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="enquiry-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="advertise">I want to advertise vacancies</SelectItem>
                        <SelectItem value="general">General question</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="en-name">Your name *</Label>
                      <Input id="en-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="enquiry-name" />
                    </div>
                    <div>
                      <Label htmlFor="en-company">Company</Label>
                      <Input id="en-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="mt-1.5" data-testid="enquiry-company" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="en-email">Email *</Label>
                      <Input id="en-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="enquiry-email" />
                    </div>
                    <div>
                      <Label htmlFor="en-phone">Phone</Label>
                      <Input id="en-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" data-testid="enquiry-phone" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="en-msg">Message *</Label>
                    <Textarea id="en-msg" required value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5 min-h-28" placeholder="Tell us a bit about your roles or your question…" data-testid="enquiry-message" />
                  </div>
                  <Button type="submit" className="w-full rounded-full" disabled={submitting} data-testid="enquiry-submit">
                    {submitting ? "Sending…" : "Send enquiry"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
